import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { Group, Tooltip } from '@mantine/core';

import { Compliance, Component } from 'models';

import { BadgePill } from 'components/badges/BadgePill';
import { CCC } from 'components/icons/certification/CCC';
import { CE } from 'components/icons/certification/CE';
import { CSA } from 'components/icons/certification/CSA';
import { UL } from 'components/icons/certification/UL';

import { BadgeGroup } from './BadgeGroup';

import cx from './Compliances.module.scss';

type ComplianceBadge = {
    icon: React.ReactNode;
    tooltip?: React.ReactNode;
};

const compliances: Partial<Record<Compliance, ComplianceBadge>> = {
    [Compliance.CE]: {
        icon: <CE size={20} />,
        tooltip: 'This product is CE certified',
    },
    [Compliance.UL]: {
        icon: <UL size={20} />,
        tooltip: 'This product is UL certified',
    },
    [Compliance.CSA]: {
        icon: <CSA size={20} />,
        tooltip: 'This product is CSA certified',
    },
    [Compliance.CCC]: {
        icon: <CCC size={20} />,
        tooltip: 'This product is CCC certified',
    },
};

const Compliances: FC = () => {
    const compliance = useWatch({
        name: 'compliance',
    }) as Component['compliance'];

    const currentCompliances = Object.entries(compliances).filter(([key]) => compliance?.[key as Compliance]);

    if (currentCompliances.length === 0) {
        return null;
    }

    return (
        <BadgeGroup label="Designed to">
            <Group align="stretch" gap="xs">
                {currentCompliances.map(([key, badge]) => (
                    <ComplianceBadge tooltip={badge.tooltip} key={key}>
                        {badge.icon}
                    </ComplianceBadge>
                ))}
            </Group>
        </BadgeGroup>
    );
};

const ComplianceBadge = ({ children, tooltip }: { children: React.ReactNode; tooltip?: React.ReactNode }) => {
    return (
        <Tooltip label={tooltip} disabled={!tooltip}>
            <BadgePill className={cx.badge}>{children}</BadgePill>
        </Tooltip>
    );
};

export { Compliances };
